/**
 * CDM Threat-Actor Attribution Lens — Track 3.14.
 *
 * Group CISO morning summary: actors most likely targeting our sector ×
 * their TTPs × which of our assets are vulnerable to those specific TTPs.
 *
 * Three-pane layout (org-level analogue of CdmInvestigationPage):
 *   Left rail   actors targeting our sector
 *   Centre      TTPs of the selected actor (MITRE-style cards)
 *   Right rail  our assets vulnerable to the selected TTP
 *
 * Axonius cannot render this view because they don't have the threat-actor
 * catalogue on the join's other side. We do — 969 MITRE+MISP actors.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Crosshair, Users, Target, RefreshCw, ChevronLeft, ExternalLink,
  Shield, Globe, Activity, Skull,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface ActorRow {
  actor_id: string
  name: string
  aliases: string[]
  country: string
  sponsor_code: string
  category: string
  sectors: string[]
  techniques: string[]
  technique_count: number
  known_cve_count: number
  recent_mention_count: number
  last_mentioned_at: string | null
}

interface Ttp {
  t_code: string
  name: string
  tactic: string
  tactic_label: string
  description?: string
  actor_count?: number
  our_asset_count?: number
  our_assets_count?: number
  our_unpatched_cve_count?: number
}

interface HotSpot {
  actor_id: string
  name: string
  country: string
  category: string
  asset_count: number
  technique_count: number
  recent_mention_count: number
}

interface Summary {
  sector: string
  country: string
  lookback_days: number
  actors: ActorRow[]
  ttps: Ttp[]
  hot_spots: HotSpot[]
  generated_at: string
}

interface ActorDetail {
  actor: {
    id: string
    name: string
    aliases: string[]
    country: string
    sponsor_code: string
    category: string
    motivation: string
    description: string
    sectors: string[]
    software: string[]
    known_cves: string[]
    refs: string[]
    sources: string[]
  }
  ttps: Ttp[]
  ttp_count: number
}

interface VulnAsset {
  asset_id: string
  hostname: string | null
  vendor: string | null
  product: string | null
  version: string | null
  cve_id: string
  cvss_v3: number | null
  severity: string | null
  attributed_actor: string
}

interface TtpDetail {
  ttp: Ttp
  actors: string[]
  actor_count: number
  asset_count: number
  assets: VulnAsset[]
}

// ATT&CK tactic colour bands — match MITRE Navigator conventions
const TACTIC_TONE: Record<string, string> = {
  'reconnaissance':       'bg-cyan/15 text-cyan border-cyan/40',
  'resource-development': 'bg-cyan/10 text-cyan border-cyan/30',
  'initial-access':       'bg-amber/20 text-amber-light border-amber/40',
  'execution':            'bg-rose/20 text-rose-light border-rose/40',
  'persistence':          'bg-purple/20 text-purple-light border-purple/40',
  'privilege-escalation': 'bg-purple/15 text-purple-light border-purple/40',
  'defense-evasion':      'bg-blue/15 text-blue-light border-blue/40',
  'credential-access':    'bg-amber/15 text-amber-light border-amber/40',
  'discovery':            'bg-emerald/10 text-emerald border-emerald/30',
  'lateral-movement':     'bg-rose/15 text-rose-light border-rose/30',
  'collection':           'bg-cyan/20 text-cyan border-cyan/40',
  'command-and-control':  'bg-purple/20 text-purple-light border-purple/40',
  'exfiltration':         'bg-rose/25 text-rose-light border-rose/40',
  'impact':               'bg-rose/30 text-rose-light border-rose/50',
}

const CATEGORY_TONE: Record<string, string> = {
  'nation-state': 'bg-rose/15 text-rose-light border-rose/30',
  'ransomware':   'bg-amber/15 text-amber-light border-amber/30',
  'criminal':     'bg-purple/10 text-purple-light border-purple/30',
  'hacktivist':   'bg-cyan/10 text-cyan border-cyan/30',
  'unknown':      'bg-surface-raised text-text-muted border-border',
}

function flagFor(code: string): string {
  if (!code || code.length !== 2) return ''
  // ISO-3166 alpha-2 → emoji flag (regional indicator symbols)
  const A = 0x1f1e6
  const c0 = code.toUpperCase().charCodeAt(0) - 65
  const c1 = code.toUpperCase().charCodeAt(1) - 65
  if (c0 < 0 || c0 > 25 || c1 < 0 || c1 > 25) return ''
  return String.fromCodePoint(A + c0) + String.fromCodePoint(A + c1)
}

export default function CdmAttributionLensPage() {
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedActor, setSelectedActor] = useState<string | null>(null)
  const [actorDetail, setActorDetail] = useState<ActorDetail | null>(null)
  const [actorLoading, setActorLoading] = useState(false)
  const [selectedTtp, setSelectedTtp] = useState<string | null>(null)
  const [ttpDetail, setTtpDetail] = useState<TtpDetail | null>(null)
  const [ttpLoading, setTtpLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/cdm/threat-attribution')
      .then(r => setSummary(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    refresh()
  }, [])

  useEffect(() => {
    if (!selectedActor) {
      setActorDetail(null)
      return
    }
    setActorLoading(true)
    api.get(`/api/cdm/threat-attribution/actor/${encodeURIComponent(selectedActor)}`)
      .then(r => setActorDetail(r.data))
      .catch(() => setActorDetail(null))
      .finally(() => setActorLoading(false))
    setSelectedTtp(null)
    setTtpDetail(null)
  }, [selectedActor])

  useEffect(() => {
    if (!selectedTtp) {
      setTtpDetail(null)
      return
    }
    setTtpLoading(true)
    api.get(`/api/cdm/threat-attribution/ttp/${encodeURIComponent(selectedTtp)}`)
      .then(r => setTtpDetail(r.data))
      .catch(() => setTtpDetail(null))
      .finally(() => setTtpLoading(false))
  }, [selectedTtp])

  const heroStats = useMemo(() => {
    if (!summary) {
      return { actors: 0, ttps: 0, exposed: 0 }
    }
    const exposed = summary.ttps.reduce((m, t) => Math.max(m, t.our_asset_count || 0), 0)
    return {
      actors: summary.actors.length,
      ttps:   summary.ttps.filter(t => (t.our_asset_count || 0) > 0).length,
      exposed,
    }
  }, [summary])

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Crosshair size={18} className="text-rose-light" /> THREAT-ACTOR ATTRIBUTION
        </h1>
        {summary && (
          <span className="text-[11px] font-mono text-text-secondary">
            sector:{summary.sector} · country:{summary.country} · last {summary.lookback_days}d
          </span>
        )}
        <button
          className="ml-auto inline-flex items-center gap-1 text-xs text-text-muted hover:text-text-primary"
          onClick={refresh}
          disabled={loading}
        >
          <RefreshCw size={13} className={clsx(loading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Hero stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <HeroTile icon={Users}    label="Actors targeting us"   value={heroStats.actors}  tone="text-rose-light" />
        <HeroTile icon={Target}   label="High-exposure TTPs"    value={heroStats.ttps}    tone="text-amber-light" />
        <HeroTile icon={Shield}   label="Vulnerable assets"     value={heroStats.exposed} tone="text-purple-light" />
        <HeroTile icon={Activity} label="Last refreshed"
                  value={summary?.generated_at ? new Date(summary.generated_at).toLocaleTimeString() : '—'}
                  tone="text-text-secondary" small />
      </div>

      {/* Hot-spots ribbon */}
      {summary && summary.hot_spots && summary.hot_spots.filter(h => h.asset_count > 0).length > 0 && (
        <div className="card p-3">
          <h2 className="text-[11px] uppercase tracking-widest text-text-muted mb-2 flex items-center gap-2">
            <Skull size={12} className="text-rose-light" /> Hot-spots — actors with most matching unpatched assets
          </h2>
          <div className="flex flex-wrap gap-2">
            {summary.hot_spots.filter(h => h.asset_count > 0).slice(0, 6).map(h => (
              <button
                key={h.actor_id}
                onClick={() => setSelectedActor(h.actor_id)}
                className={clsx(
                  'badge text-[10px] border px-2 py-1 hover:border-rose/60',
                  selectedActor === h.actor_id ? 'bg-rose/15 text-rose-light border-rose/50'
                                                : 'bg-surface-raised text-text-secondary border-border',
                )}
              >
                <span className="font-semibold">{h.name}</span>
                <span className="text-text-muted ml-1">{h.asset_count} assets</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Three-pane grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT — actor list */}
        <div className="lg:col-span-3 space-y-2">
          <div className="card p-3">
            <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-2">
              <Users size={14} className="text-rose-light" /> Actors targeting our sector
            </h2>
            {loading && !summary && (
              <p className="text-xs text-text-muted">Loading…</p>
            )}
            {summary && summary.actors.length === 0 && (
              <p className="text-xs text-text-muted">No catalogued actors flag this sector.</p>
            )}
            <div className="space-y-1.5 max-h-[70vh] overflow-y-auto pr-1">
              {summary?.actors.map(a => (
                <button
                  key={a.actor_id}
                  onClick={() => setSelectedActor(a.actor_id)}
                  className={clsx(
                    'w-full text-left p-2 rounded border transition-colors',
                    selectedActor === a.actor_id
                      ? 'bg-rose/10 border-rose/40'
                      : 'bg-surface-raised border-border hover:border-rose/30',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base leading-none">{flagFor(a.sponsor_code)}</span>
                    <span className="font-semibold text-text-primary text-xs flex-1 truncate">{a.name}</span>
                    <span className="text-[10px] font-mono text-text-muted">{a.recent_mention_count}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    <span className={clsx('badge text-[9px] border px-1.5',
                                          CATEGORY_TONE[a.category] || CATEGORY_TONE.unknown)}>
                      {a.category}
                    </span>
                    <span className="text-[9px] text-text-muted font-mono">{a.country}</span>
                    {a.technique_count > 0 && (
                      <span className="text-[9px] text-text-muted">· {a.technique_count} TTPs</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CENTRE — TTPs of selected actor */}
        <div className="lg:col-span-5 space-y-2">
          <div className="card p-3 min-h-[300px]">
            {!selectedActor && (
              <div className="text-center text-text-muted text-sm py-12">
                <Target size={28} className="mx-auto mb-2 opacity-40" />
                Select an actor to see their TTPs.
              </div>
            )}
            {selectedActor && actorLoading && (
              <p className="text-xs text-text-muted">Loading actor…</p>
            )}
            {actorDetail && (
              <>
                <div className="border-b border-border pb-2 mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base">{flagFor(actorDetail.actor.sponsor_code)}</span>
                    <h2 className="text-sm font-bold text-text-primary">{actorDetail.actor.name}</h2>
                    <span className={clsx('badge text-[10px] border',
                                          CATEGORY_TONE[actorDetail.actor.category] || CATEGORY_TONE.unknown)}>
                      {actorDetail.actor.category}
                    </span>
                    <span className="text-[10px] font-mono text-text-muted">
                      <Globe size={10} className="inline mr-1" />{actorDetail.actor.country}
                    </span>
                  </div>
                  {actorDetail.actor.aliases.length > 0 && (
                    <div className="text-[10px] text-text-muted mt-1">
                      aka {actorDetail.actor.aliases.slice(0, 6).join(', ')}
                    </div>
                  )}
                  {actorDetail.actor.description && (
                    <p className="text-[11px] text-text-secondary mt-2 leading-relaxed">
                      {actorDetail.actor.description.slice(0, 360)}
                      {actorDetail.actor.description.length > 360 ? '…' : ''}
                    </p>
                  )}
                </div>

                <h3 className="text-[11px] uppercase tracking-widest text-text-muted mb-2 flex items-center gap-1">
                  <Target size={11} className="text-amber-light" />
                  TTPs · {actorDetail.ttp_count} techniques
                </h3>
                {actorDetail.ttps.length === 0 ? (
                  <p className="text-xs text-text-muted">No MITRE techniques mapped to this actor.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55vh] overflow-y-auto pr-1">
                    {actorDetail.ttps.map(t => {
                      const expo = t.our_assets_count || 0
                      return (
                        <button
                          key={t.t_code}
                          onClick={() => setSelectedTtp(t.t_code)}
                          className={clsx(
                            'text-left p-2 rounded border transition-colors',
                            selectedTtp === t.t_code
                              ? 'bg-amber/15 border-amber/50'
                              : 'bg-surface-raised border-border hover:border-amber/40',
                          )}
                        >
                          <div className="flex items-center justify-between gap-1 mb-1">
                            <code className="font-mono text-[10px] text-cyan">{t.t_code}</code>
                            {expo > 0 && (
                              <span className="badge text-[9px] bg-rose/15 text-rose-light border-rose/40 border">
                                {expo} assets
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] font-semibold text-text-primary line-clamp-2">{t.name}</div>
                          <span className={clsx(
                            'badge text-[9px] border px-1.5 mt-1 inline-block',
                            TACTIC_TONE[t.tactic] || 'bg-surface-raised text-text-muted border-border',
                          )}>
                            {t.tactic_label || t.tactic}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* RIGHT — vulnerable assets for selected TTP */}
        <div className="lg:col-span-4 space-y-2">
          <div className="card p-3 min-h-[300px]">
            {!selectedTtp && (
              <div className="text-center text-text-muted text-sm py-12">
                <Shield size={28} className="mx-auto mb-2 opacity-40" />
                Select a TTP to see your exposure.
              </div>
            )}
            {selectedTtp && ttpLoading && (
              <p className="text-xs text-text-muted">Loading exposure…</p>
            )}
            {ttpDetail && (
              <>
                <div className="border-b border-border pb-2 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="font-mono text-xs text-cyan">{ttpDetail.ttp.t_code}</code>
                    <span className={clsx(
                      'badge text-[9px] border px-1.5',
                      TACTIC_TONE[ttpDetail.ttp.tactic] || 'bg-surface-raised text-text-muted border-border',
                    )}>
                      {ttpDetail.ttp.tactic_label || ttpDetail.ttp.tactic}
                    </span>
                  </div>
                  <h2 className="text-sm font-bold text-text-primary">{ttpDetail.ttp.name}</h2>
                  {ttpDetail.ttp.description && (
                    <p className="text-[11px] text-text-secondary mt-1.5 leading-relaxed">
                      {ttpDetail.ttp.description.slice(0, 240)}
                      {ttpDetail.ttp.description.length > 240 ? '…' : ''}
                    </p>
                  )}
                  <div className="text-[10px] text-text-muted mt-1.5">
                    Used by {ttpDetail.actor_count} catalogued actor{ttpDetail.actor_count === 1 ? '' : 's'}
                  </div>
                </div>

                <h3 className="text-[11px] uppercase tracking-widest text-text-muted mb-2 flex items-center gap-1">
                  <Shield size={11} className="text-rose-light" />
                  Your exposure · {ttpDetail.asset_count} assets
                </h3>
                {ttpDetail.assets.length === 0 ? (
                  <p className="text-xs text-emerald">No CDM assets currently match the CVEs attributed to this TTP.</p>
                ) : (
                  <div className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
                    {ttpDetail.assets.map((a, i) => (
                      <Link
                        key={`${a.asset_id}-${a.cve_id}-${i}`}
                        to={`/app/cdm/investigation/${a.asset_id}`}
                        className="block p-2 rounded border border-border bg-surface-raised hover:border-rose/40 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold text-text-primary truncate">
                            {a.hostname || a.asset_id.slice(0, 8)}
                          </span>
                          <ExternalLink size={10} className="text-text-muted shrink-0" />
                        </div>
                        <div className="text-[10px] text-text-secondary font-mono truncate">
                          {[a.vendor, a.product, a.version].filter(Boolean).join(':') || '—'}
                        </div>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <span className="text-[9px] font-mono text-cyan">{a.cve_id}</span>
                          {a.cvss_v3 != null && (
                            <span className={clsx(
                              'text-[9px] font-mono',
                              a.cvss_v3 >= 9 ? 'text-rose-light' :
                              a.cvss_v3 >= 7 ? 'text-amber-light' :
                              a.cvss_v3 >= 4 ? 'text-cyan' : 'text-text-muted',
                            )}>
                              CVSS {a.cvss_v3.toFixed(1)}
                            </span>
                          )}
                          <span className="text-[9px] text-text-muted">via {a.attributed_actor}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


function HeroTile({
  icon: Icon, label, value, tone, small,
}: {
  icon: React.ElementType
  label: string
  value: string | number
  tone: string
  small?: boolean
}) {
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted flex items-center gap-1">
        <Icon size={11} className={tone} /> {label}
      </div>
      <div className={clsx('font-mono font-black mt-1', tone, small ? 'text-base' : 'text-2xl')}>
        {value}
      </div>
    </div>
  )
}
